import { useState, useMemo, useCallback } from "react";
import { useForm, useController } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import { useWalletBalances, useTokenValidation } from "@/hooks";
import type * as yup from "yup";
import { TOKEN_AMOUNT_KEY, LOCK_DURATION_KEY, type TokenFormValues, type TokenId } from "@/types";
import { isExpiredToken } from "@/config";

interface UseTokenFormProps {
    defaultTokenId: TokenId;
    extendSchema?: yup.AnyObjectSchema;
    initialAmount?: number;
}

const reValidateMode = "onSubmit";

export function useTokenForm({ defaultTokenId, extendSchema, initialAmount }: UseTokenFormProps) {
    const [selectedTokenId, setSelectedTokenId] = useState<TokenId>(defaultTokenId);
    const { tokenBalances, isPending } = useWalletBalances();

    const selectTokenId = useCallback(
        (tokenId: TokenId) => {
            if (isExpiredToken(tokenId)) return;
            setSelectedTokenId(tokenId);
        },
        [setSelectedTokenId],
    );

    const tokenSchema = useTokenValidation({ balance: tokenBalances[selectedTokenId]?.tokenBalance ?? "" });

    const finalSchema = useMemo(
        () => (extendSchema ? tokenSchema.concat(extendSchema) : tokenSchema),
        [tokenSchema, extendSchema],
    );

    const resolver = useMemo(() => yupResolver(finalSchema), [finalSchema]);

    const form = useForm<TokenFormValues>({
        resolver,
        mode: reValidateMode,
        reValidateMode,
    });

    const {
        field: amountField,
        fieldState: { error: amountError },
    } = useController({
        name: TOKEN_AMOUNT_KEY,
        control: form.control,
        defaultValue: initialAmount ? String(initialAmount) : "",
    });

    // Create the onChange handler separately
    const handleAmountChange = useCallback(
        (value: string) => {
            form.clearErrors();
            amountField.onChange(value);
        },
        [form, amountField],
    );

    // Then use it in the amount object
    const amount = useMemo(
        () => ({
            ...amountField,
            onChange: handleAmountChange,
        }),
        [amountField, handleAmountChange],
    );

    const selectedTokenBalance = tokenBalances[selectedTokenId]?.tokenBalance ?? "";

    const setMaxAmount = useCallback(() => {
        if (!selectedTokenBalance) return handleAmountChange("");

        // Not a decimal
        if (!selectedTokenBalance.includes(".")) {
            handleAmountChange(selectedTokenBalance);
            return;
        }

        // Truncate decimals to protect number corruption
        const [integer, decimal] = selectedTokenBalance.split(".");
        if (decimal && decimal.length > 6) {
            handleAmountChange(`${integer}.${decimal.slice(0, 6)}`);
        } else {
            handleAmountChange(selectedTokenBalance);
        }
    }, [handleAmountChange, selectedTokenBalance]);

    const resetAmount = useCallback(() => {
        handleAmountChange("");
    }, [handleAmountChange]);

    const isMaxDisabled = useMemo(() => {
        if (isPending) return true;
        const numberTokenBalance = Number(selectedTokenBalance);
        return !numberTokenBalance || numberTokenBalance <= 0;
    }, [selectedTokenBalance, isPending]);

    const isAmountOverBalance = useMemo(() => {
        const parsedAmount = Number(amount.value);
        const parsedBalance = Number(selectedTokenBalance);

        if (!Number.isFinite(parsedAmount) || !Number.isFinite(parsedBalance)) {
            return false;
        }

        return parsedAmount > parsedBalance;
    }, [amount.value, selectedTokenBalance]);

    return {
        form,
        amount,
        amountError,
        selectedTokenId,
        selectedTokenBalance,
        isPending,
        isMaxDisabled,
        isAmountOverBalance,
        selectTokenId,
        setMaxAmount,
        resetAmount,
        TOKEN_AMOUNT_KEY,
        LOCK_DURATION_KEY,
    };
}
